import { signal } from '@preact/signals';
import type { ComponentChildren } from 'preact';
import { audio } from '../../game/audio/Audio';

export type Screen =
  | 'lobby'
  | 'play'
  | 'results'
  | 'armory'
  | 'wardrobe'
  | 'store'
  | 'battlepass'
  | 'quests'
  | 'achievements'
  | 'friends'
  | 'clan'
  | 'leaderboards'
  | 'inbox'
  | 'settings'
  | 'legal';

export interface Route {
  screen: Screen;
  params: Record<string, string>;
}

export const route = signal<Route>({ screen: 'lobby', params: {} });
const stack: Route[] = [];

export function go(screen: Screen, params: Record<string, string> = {}, sound = true): void {
  if (sound) audio.play('ui_click');
  const cur = route.value;
  if (cur.screen === screen && JSON.stringify(cur.params) === JSON.stringify(params)) return;
  if (screen === 'lobby') stack.length = 0;
  else stack.push(cur);
  if (stack.length > 12) stack.splice(0, stack.length - 12);
  route.value = { screen, params };
}

/** Replaces the current route without growing the back stack (tabs inside a screen). */
export function replace(screen: Screen, params: Record<string, string> = {}): void {
  route.value = { screen, params };
}

export function back(): void {
  audio.play('ui_click');
  route.value = stack.pop() ?? { screen: 'lobby', params: {} };
}

export function home(): void {
  stack.length = 0;
  route.value = { screen: 'lobby', params: {} };
}

// Modals -------------------------------------------------------------------------------------------------

export interface ModalEntry {
  id: number;
  render: (close: () => void) => ComponentChildren;
  /** Tapping the backdrop / pressing Escape closes the modal (default true). */
  dismissable: boolean;
  /** Called when the modal is dismissed (backdrop, Escape) rather than closed by its own buttons. */
  onDismiss?: () => void;
}

export const modals = signal<ModalEntry[]>([]);
let modalSeq = 1;

export function openModal(render: (close: () => void) => ComponentChildren, opts: { dismissable?: boolean; onDismiss?: () => void } = {}): () => void {
  const id = modalSeq++;
  modals.value = [...modals.value, { id, render, dismissable: opts.dismissable !== false, onDismiss: opts.onDismiss }];
  return () => closeModal(id);
}

export function closeModal(id: number): void {
  modals.value = modals.value.filter((m) => m.id !== id);
}

export function dismissModal(id: number): void {
  const m = modals.value.find((x) => x.id === id);
  if (!m || !m.dismissable) return;
  closeModal(id);
  m.onDismiss?.();
}

export function closeTopModal(): boolean {
  const list = modals.value;
  const top = list[list.length - 1];
  if (!top || !top.dismissable) return false;
  dismissModal(top.id);
  return true;
}
